// Icon: material-symbols:animated-images
// Slug: Animates element attributes over time.
// Description: Allows you to animate element attributes over time. Animated attributes are updated reactively whenever signals used in the expression change.

import { attribute } from '@engine'
import { effect } from '@engine/signals'
import { inverseLerp, lerp } from '@utils/math'
import { tagFirst, tagToMs } from '@utils/tags'

attribute({
  name: 'animate',
  requirement: {
    value: 'must',
  },
  returnsValue: true,
  apply({ el, key, mods, rx }) {
    let duration = 1000
    const durationArgs = mods.get('duration')
    if (durationArgs) {
      duration = tagToMs(durationArgs)
    }
    const easingName = tagFirst(mods.get('ease'), 'linear')
    const easing = Easing[easingName]
    if (!easing) {
      throw new Error(
        `Invalid easing function: ${easingName}. Available easing functions: ${Object.keys(Easing).join(', ')}`,
      )
    }
    let delay = 0
    const delayArgs = mods.get('delay')
    if (delayArgs) {
      delay = tagToMs(delayArgs)
    }
    let animId = -1
    const cancelAnimation = () => {
      if (animId === -1) return
      cancelAnimationFrame(animId)
      animId = -1
    }
    const startAttributeAnimation = (key: string, targetValueRaw: string) => {
      const end = toSuffixedValue(targetValueRaw)
      const startAttrRaw = el.getAttribute(key) || `0${end.suffix}`
      if (startAttrRaw === targetValueRaw) {
        el.setAttribute(key, targetValueRaw)
        return
      }
      const start = toSuffixedValue(startAttrRaw)
      if (start.suffix !== end.suffix) {
        throw new Error(
          `Cannot animate attribute "${key}" from "${startAttrRaw}" to "${targetValueRaw}" because they have different suffixes: "${start.suffix}" and "${end.suffix}".`,
        )
      }
      cancelAnimation()
      let startTime = 0
      let endTime = 0
      const updateAttribute = () => {
        const now = performance.now()
        let isFinished = now >= endTime
        let revisedValue = 0
        if (isFinished) {
          revisedValue = end.value
          const shouldLoop = mods.has('loop')
          const shouldPingPong = mods.has('pingpong')
          if (shouldLoop || shouldPingPong) {
            startTime = now
            endTime = now + duration
            if (shouldPingPong) {
              const temp = start.value
              start.value = end.value
              end.value = temp
            }
            revisedValue = start.value
            isFinished = false
          }
        } else {
          const baseT = inverseLerp(startTime, endTime, now)
          const easedT = easing(baseT)
          revisedValue = lerp(start.value, end.value, easedT)
        }
        el.setAttribute(key, `${revisedValue}${end.suffix}`)
        if (isFinished) cancelAnimation()
        else {
          animId = requestAnimationFrame(updateAttribute)
        }
      }
      setTimeout(() => {
        startTime = performance.now()
        endTime = startTime + duration
        animId = requestAnimationFrame(updateAttribute)
      }, delay)
    }
    if (!key) {
      return effect(() => {
        const binds = rx() as Record<string, string>
        for (const [key, val] of Object.entries(binds)) {
          startAttributeAnimation(key, val)
        }
      })
    }
    return effect(() => {
      const value = rx()
      startAttributeAnimation(key, `${value}`)
    })
  },
})

type SuffixedValue = {
  value: number
  suffix: string
}

const toSuffixedValue = (value: string): SuffixedValue => {
  const match = value.match(/^(-?\d*\.?\d+)([a-z%]*)$/i)
  if (match) {
    return {
      value: Number.parseFloat(match[1]),
      suffix: match[2] || '',
    }
  }
  throw new Error(`Invalid suffixed value: ${value}`)
}

const { sqrt, sin, cos, PI } = Math

type EasingFunction = (t: number) => number

const Easing: Record<string, EasingFunction> = {
  // No easing, no acceleration
  linear: (t: number) => t,

  // Accelerates fast, then slows quickly towards end
  quadratic: (t: number) => t * (-(t * t) * t + 4 * t * t - 6 * t + 4),

  // Overshoots over 1 and then returns to 1 towards end.
  cubic: (t: number) => t * (4 * t * t - 9 * t + 6),

  // Overshoots over 1 multiple times - wiggles around 1.
  elastic: (t: number) =>
    t * (33 * t * t * t * t - 106 * t * t * t + 126 * t * t - 67 * t + 15),

  // Accelerating from zero velocity
  inquad: (t: number) => t * t,

  // Decelerating to zero velocity
  outquad: (t: number) => t * (2 - t),

  // Acceleration until halfway, then deceleration
  inoutquad: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),

  // Accelerating from zero velocity
  incubic: (t: number) => t * t * t,

  // Decelerating to zero velocity
  outcubic: (t: number) => --t * t * t + 1,

  // Acceleration until halfway, then deceleration
  inoutcubic: (t: number) =>
    t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,

  // Accelerating from zero velocity
  inquart: (t: number) => t * t * t * t,

  // Decelerating to zero velocity
  outquart: (t: number) => 1 - --t * t * t * t,

  // Acceleration until halfway, then deceleration
  inoutquart: (t: number) =>
    t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t,

  // Accelerating from zero velocity
  inquint: (t: number) => t * t * t * t * t,

  // Decelerating to zero velocity
  outquint: (t: number) => 1 + --t * t * t * t * t,

  // Acceleration until halfway, then deceleration
  inoutquint: (t: number) =>
    t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * --t * t * t * t * t,

  // Accelerating from zero velocity
  insine: (t: number) => -cos(t * (PI / 2)) + 1,

  // Decelerating to zero velocity
  outsine: (t: number) => sin(t * (PI / 2)),

  // Accelerating until halfway, then decelerating
  inoutsine: (t: number) => -(cos(PI * t) - 1) / 2,

  // Exponential accelerating from zero velocity
  inexpo: (t: number) => 2 ** (10 * (t - 1)),

  // Exponential decelerating to zero velocity
  outexpo: (t: number) => -(2 ** (-10 * t)) + 1,

  // Exponential accelerating until halfway, then decelerating
  inoutexpo: (t: number) => {
    t /= 0.5
    if (t < 1) return 2 ** (10 * (t - 1)) / 2
    t--
    return (-(2 ** (-10 * t)) + 2) / 2
  },

  // Circular accelerating from zero velocity
  incirc: (t: number) => -sqrt(1 - t * t) + 1,

  // Circular decelerating to zero velocity Moves VERY fast at the beginning and
  // then quickly slows down in the middle. This tween can actually be used
  // in continuous transitions where target value changes all the time,
  // because of the very quick start, it hides the jitter between target value changes
  outcirc: (t: number) => sqrt(1 - (t = t - 1) * t),

  // Circular acceleration until halfway, then deceleration
  inoutcirc: (t: number) => {
    t /= 0.5
    if (t < 1) return -(sqrt(1 - t * t) - 1) / 2
    t -= 2
    return (sqrt(1 - t * t) + 1) / 2
  },

  inelastic: (t: number) => {
    const c4 = (2 * PI) / 3
    return t === 0
      ? 0
      : t === 1
        ? 1
        : -(2 ** (10 * (t - 1))) * sin((t - 1.1) * c4)
  },

  outelastic: (t: number) => {
    const c4 = (2 * PI) / 3
    return t === 0 ? 0 : t === 1 ? 1 : 2 ** (-10 * t) * sin((t - 0.1) * c4) + 1
  },

  inoutelastic: (t: number) => {
    const c5 = (2 * PI) / 4.5
    return t === 0
      ? 0
      : t === 1
        ? 1
        : t < 0.5
          ? (-(2 ** (20 * t - 10)) * sin((20 * t - 11.125) * c5)) / 2
          : (2 ** (-20 * t + 10) * sin((20 * t - 11.125) * c5)) / 2 + 1
  },

  inback: (t: number) => {
    const s = 1.70158
    return t * t * ((s + 1) * t - s)
  },
  outback: (t: number) => {
    const s = 1.70158
    return (t = t - 1) * t * ((s + 1) * t + s) + 1
  },
  inoutback: (t: number) => {
    const s = 1.70158 * 1.525
    t /= 0.5
    if (t < 1) return (t * t * ((s + 1) * t - s)) / 2
    t -= 2
    return (t * t * ((s + 1) * t + s) + 2) / 2
  },

  inbounce: (t: number) => 1 - Easing.outBounce(1 - t),
  outbounce: (t: number) => {
    if (t < 1 / 2.75) {
      return 7.5625 * t * t
    }
    if (t < 2 / 2.75) {
      t -= 1.5 / 2.75
      return 7.5625 * t * t + 0.75
    }
    if (t < 2.5 / 2.75) {
      t -= 2.25 / 2.75
      return 7.5625 * t * t + 0.9375
    }
    t -= 2.625 / 2.75
    return 7.5625 * t * t + 0.984375
  },

  inoutbounce: (t: number) => {
    if (t < 0.5) return Easing.inBounce(t * 2) / 2
    return Easing.outBounce(t * 2 - 1) / 2 + 0.5
  },

  ingolden: (t: number) => t ** 1.834057,
  outgolden: (t: number) => 1 - (1 - t) ** 1.834057,
  inoutgolden: (t: number) => {
    if (t < 0.5) return Easing.inGolden(t * 2) / 2
    return Easing.outGolden(t * 2 - 1) / 2 + 0.5
  },
}
